import { useEffect, useState } from 'react';
import { Box, Typography, Dialog, IconButton, ButtonBase } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { fetchAttachmentBlob, type ScopeAttachment } from '../../api/hooks/useScopeAttachments';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i;

/**
 * Bilderkennung für die Galerie: MIME-Typ `image/*` oder Dateiendung.
 * Bewusst unabhängig von Ordnern: die Galerie bekommt immer die flache Liste
 * aller Scope-Anhänge (später über alle Ordner, PHQ-115).
 */
export const isGalleryImage = (a: Pick<ScopeAttachment, 'mime' | 'filename'>): boolean =>
  (!!a.mime && a.mime.startsWith('image/')) || IMAGE_EXT.test(a.filename);

/** Lädt ein Anhang-Bild authentifiziert als Blob (die Route verlangt den Auth-Header). */
function BlobImage({ projectId, id, alt, style }: { projectId: string; id: string; alt: string; style?: React.CSSProperties }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    fetchAttachmentBlob(projectId, id)
      .then((blob) => { if (!cancelled) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [projectId, id]);
  if (failed) return <Typography variant="caption" color="error">{alt}</Typography>;
  if (!src) return null;
  return <img src={src} alt={alt} style={style} />;
}

/**
 * Galerie aller Bild-Anhänge des Scope Statements: Vorschaubilder im Raster,
 * Klick öffnet die Großansicht mit Blättern (Pfeile, Pfeiltasten) und Beschreibung.
 */
export function ScopeGallery({ projectId, attachments }: { projectId: string; attachments: ScopeAttachment[] }) {
  const { t } = useTranslation('projects');
  const images = attachments.filter(isGalleryImage);
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const current = openIdx != null ? images[openIdx] : undefined;
  const step = (delta: number) =>
    setOpenIdx((i) => (i == null || images.length === 0 ? i : (i + delta + images.length) % images.length));

  return (
    <Box sx={{ mt: 2 }} data-testid="scope-gallery">
      <Typography variant="subtitle2" sx={{ color: 'var(--pm-fg-muted)', fontWeight: 600, mb: 0.5 }}>
        {t('scope.gallery.title', 'Bildergalerie')}{images.length > 0 ? ` (${images.length})` : ''}
      </Typography>

      {images.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
          {t('scope.gallery.empty', 'Noch keine Bilder in den Anhängen.')}
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1 }}>
          {images.map((img, i) => (
            <ButtonBase
              key={img.id}
              aria-label={t('scope.gallery.open', 'Bild öffnen: {{name}}', { name: img.filename }).replace('{{name}}', img.filename)}
              onClick={() => setOpenIdx(i)}
              sx={{ height: 100, borderRadius: 1, overflow: 'hidden', bgcolor: 'var(--pm-bg-subtle)' }}
            >
              <BlobImage projectId={projectId} id={img.id} alt={img.description ?? img.filename}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </ButtonBase>
          ))}
        </Box>
      )}

      <Dialog
        open={!!current}
        onClose={() => setOpenIdx(null)}
        maxWidth="lg"
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        }}
      >
        {current && (
          <Box sx={{ position: 'relative', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <IconButton aria-label={t('scope.gallery.close', 'Schließen')} onClick={() => setOpenIdx(null)} sx={{ position: 'absolute', top: 8, right: 8 }}>
              <CloseIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <IconButton aria-label={t('scope.gallery.prev', 'Vorheriges Bild')} onClick={() => step(-1)} disabled={images.length < 2}>
                <ChevronLeftIcon />
              </IconButton>
              <BlobImage projectId={projectId} id={current.id} alt={current.description ?? current.filename}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              <IconButton aria-label={t('scope.gallery.next', 'Nächstes Bild')} onClick={() => step(1)} disabled={images.length < 2}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', mt: 1 }}>{current.filename}</Typography>
            {current.description && <Typography variant="body2" sx={{ mt: 0.5 }}>{current.description}</Typography>}
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', mt: 0.5 }}>{`${(openIdx ?? 0) + 1} / ${images.length}`}</Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
